import { useCallback, useEffect, useMemo, useState, type ComponentProps, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import {
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
  type ColumnDef,
  type SortingState,
} from '@tanstack/react-table'
import clsx from 'clsx'
import dayjs from 'dayjs'

import { PageLayout } from '@components/layouts/PageLayout'
import { Alert, Button, Dialog, TextInput } from '@atoms'
import { SelectField } from '@components/molecules/SelectField'
import Badge from '@components/atoms/Badge'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuIconItem,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '@components/atoms/DropdownMenu'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@components/atoms/Tooltip'
import { DataTableContent, DataTableRoot, SortableHeader } from '@components/molecules/DataTable'
import EmptyState from '@components/molecules/EmptyState'
import { SegmentedControl } from '@components/atoms/SegmentedControl'
import { SplitButton } from '@components/molecules/SplitButton'
import { Table } from '@components/molecules/Table'
import { BreakdownLine, SimpleStatCard, SplitStatCard } from '@components/molecules/StatCard'
import { PageHeaderNav } from '@molecules/PageHeaderNav'
import Icon from '@components/utils/Icon'
import { CallbackNote } from '@frontend/contexts/users_management/components/CallbackNote'
import { toast } from '@stores/toastStore'
import { FLEXIBLE_WINDOW_MINUTES, relativeDay, timeRange, whenLabel, whenRange } from '@utils/callbackTime'

import {
  ANYONE,
  LocalAsyncSelect,
  ScheduleCallbackFields,
  callbackDraftError,
  callbackDueAt,
  draftWhen,
  newCallbackDraft,
  type AssignConfig,
  type AssignablePerson,
  toDateInput,
  toTimeInput,
  type CallbackDraft,
} from '../components/ScheduleCallbackFields'
import { LogContactForm, type LoggedContact, type Outcome as CallOutcome } from '../components/LogContactForm'

/**
 * Calls (design-phase prototype)
 *
 * One page per group for everything phone-related, in four tabs: an Overview of
 * the numbers, the group's Call lists, its Calling sessions (today a separate
 * sidebar page, consolidated here) and Callback requests. Tabs are the PageHeaderNav
 * strip under the page header, as on the inbox pages.
 *
 * Callback sample data is shaped like the draft `follow_ups` schema
 * (docs/follow_ups.dbml). Nothing here persists.
 */

// ---------- Types ----------

// Captains and vice captains behave the same here; only admins can assign a callback to someone else.
// Leaders are group captains and vice captains.
type Role = 'leader' | 'admin'
// Whose callbacks to show. Leaders never see ones assigned to other leaders.
type CallbackScope = 'me' | 'groups' | 'all'
type TabValue = 'overview' | 'call_lists' | 'calling_sessions' | 'callbacks'
type Leader = { id: string; name: string }
type Supporter = { id: string; fullName: string; phone: string }
type Origin = { kind: 'call_list' | 'action_page' | 'manual'; label: string }
type Outcome = CallOutcome | 'marked_done'

export type Callback = {
  id: string
  supporter: Supporter
  // null = anyone leading the group can take it
  assignee: Leader | null
  // null = the supporter asked for it themselves
  requestedBy: Leader | null
  requestedAt: string
  origin: Origin
  dueAt: string
  // OK to call a bit before or after dueAt
  flexible: boolean
  notes: string
  attempts: number
  reschedules: number
  lastOutcome?: Outcome
  completedAt?: string
  completedBy?: Leader
}

export type CallbackStatus = 'overdue' | 'due_now' | 'today' | 'upcoming' | 'done'
// 'done' only feeds the stats; completed callbacks never show in the list.
type Filter = 'open' | 'overdue' | 'today' | 'week' | 'done'

type CallList = {
  id: string
  name: string
  sublists: number
  supporters: number
  createdBy: Leader
  createdAt: string
}
type SessionStatus = 'completed' | 'in_progress' | 'started' | 'not_started'
type CallingSession = {
  id: string
  name: string
  callList: string
  leader: Leader
  completed: number
  total: number
  createdAt: string
  status: SessionStatus
}

// ---------- Sample data ----------

const GROUP = { name: 'Milano Centro' }
const ME: Leader = { id: 'u1', name: 'Chiara Conti' }
const LEADERS: Leader[] = [ME, { id: 'u2', name: 'Marco Ferri' }, { id: 'u3', name: 'Sofia Greco' }]
const ADMIN: Leader = { id: 'u4', name: 'Alessandro Moretti' }
// Who an admin can assign a callback to. Viewing as an admin, you are one.
const ASSIGNABLE: AssignablePerson[] = [
  { ...ME, kind: 'admin' },
  { ...ADMIN, kind: 'admin' },
  ...LEADERS.filter((l) => l.id !== ME.id).map((l) => ({ ...l, kind: 'leader' as const })),
]

const SUPPORTERS: Supporter[] = [
  { id: 's1', fullName: 'Maria Rossi', phone: '0412 345 678' },
  { id: 's2', fullName: 'Luca Bianchi', phone: '0423 456 789' },
  { id: 's3', fullName: 'Giulia Esposito', phone: '0434 567 890' },
  { id: 's4', fullName: 'Paolo Romano', phone: '0445 678 901' },
  { id: 's5', fullName: 'Elena Colombo', phone: '0456 789 012' },
  { id: 's6', fullName: 'Davide Ricci', phone: '0467 890 123' },
  { id: 's7', fullName: 'Sara Marino', phone: '0478 901 234' },
  { id: 's8', fullName: 'Andrea Gallo', phone: '0489 012 345' },
  { id: 's9', fullName: 'Francesca Bruno', phone: '0490 123 456' },
  { id: 's10', fullName: 'Matteo Costa', phone: '0401 234 567' },
  { id: 's11', fullName: 'Giorgio Lombardi', phone: '0413 579 246' },
]

// Times are relative to page load so every status has something in it.
const QUARTER_HOUR = 15 * 60_000
const at = (hoursFromNow: number) =>
  new Date(Math.round((Date.now() + hoursFromNow * 3_600_000) / QUARTER_HOUR) * QUARTER_HOUR).toISOString()
const dayAt = (daysFromToday: number, hour: number) => {
  const d = new Date()
  d.setDate(d.getDate() + daysFromToday)
  d.setHours(hour, 0, 0, 0)
  return d.toISOString()
}

const CALL_LISTS: CallList[] = [
  {
    id: 'cl1',
    name: 'October volunteer drive',
    sublists: 2,
    supporters: 142,
    createdBy: ME,
    createdAt: dayAt(-12, 10),
  },
  {
    id: 'cl2',
    name: 'Rally turnout: Saturday',
    sublists: 1,
    supporters: 88,
    createdBy: LEADERS[2],
    createdAt: dayAt(-5, 9),
  },
  {
    id: 'cl3',
    name: 'Lapsed donors check-in',
    sublists: 0,
    supporters: 64,
    createdBy: LEADERS[1],
    createdAt: dayAt(-20, 15),
  },
  {
    id: 'cl4',
    name: 'New members welcome',
    sublists: 0,
    supporters: 31,
    createdBy: ME,
    createdAt: dayAt(-2, 11),
  },
  {
    id: 'cl5',
    name: 'Doorknock follow-ups',
    sublists: 0,
    supporters: 57,
    createdBy: LEADERS[2],
    createdAt: dayAt(-1, 15),
  },
]

const CALLING_SESSIONS: CallingSession[] = [
  {
    id: 'cs1',
    name: 'Tuesday evening calls',
    callList: CALL_LISTS[0].name,
    leader: ME,
    completed: 34,
    total: 60,
    createdAt: dayAt(0, 17),
    status: 'in_progress',
  },
  {
    id: 'cs2',
    name: 'Weekend push',
    callList: CALL_LISTS[1].name,
    leader: LEADERS[2],
    completed: 12,
    total: 88,
    createdAt: dayAt(-1, 10),
    status: 'started',
  },
  {
    id: 'cs3',
    name: 'Welcome calls',
    callList: CALL_LISTS[3].name,
    leader: ME,
    completed: 0,
    total: 31,
    createdAt: dayAt(-1, 12),
    status: 'not_started',
  },
  {
    id: 'cs4',
    name: 'Donor thank-yous',
    callList: CALL_LISTS[2].name,
    leader: LEADERS[1],
    completed: 64,
    total: 64,
    createdAt: dayAt(-6, 18),
    status: 'completed',
  },
  {
    id: 'cs5',
    name: 'Volunteer drive, round 1',
    callList: CALL_LISTS[0].name,
    leader: ME,
    completed: 60,
    total: 60,
    createdAt: dayAt(-9, 18),
    status: 'completed',
  },
]

const CALL_LIST: Origin = { kind: 'call_list', label: CALL_LISTS[0].name }
const ACTION_PAGE: Origin = { kind: 'action_page', label: '"Talk to an organiser" action page' }

const callback = (
  id: string,
  supporter: Supporter,
  fields: Partial<Callback> & Pick<Callback, 'dueAt' | 'assignee' | 'requestedAt'>
): Callback => ({
  id,
  supporter,
  requestedBy: fields.assignee,
  origin: CALL_LIST,
  notes: '',
  flexible: false,
  attempts: 0,
  reschedules: 0,
  ...fields,
})

export const INITIAL_CALLBACKS: Callback[] = [
  callback('cb1', SUPPORTERS[1], {
    assignee: ME,
    requestedAt: dayAt(-3, 18),
    dueAt: dayAt(-1, 18),
    notes: 'Wants to talk about the volunteer roster.',
  }),
  callback('cb2', SUPPORTERS[3], {
    assignee: LEADERS[1],
    requestedAt: dayAt(-4, 11),
    dueAt: dayAt(-2, 9),
    notes: 'Might host a house meeting.',
    attempts: 1,
    reschedules: 1,
    lastOutcome: 'no_answer',
  }),
  callback('cb3', SUPPORTERS[0], {
    assignee: null,
    requestedBy: null,
    requestedAt: at(-5),
    origin: ACTION_PAGE,
    dueAt: at(-0.25),
    notes: 'Questions about getting to the rally.',
  }),
  callback('cb4', SUPPORTERS[2], {
    assignee: ME,
    requestedAt: dayAt(-1, 14),
    dueAt: at(0),
    notes:
      'Evenings after 6, wants the event details. Partner is Fabio, he may pick up instead, so ask for Giulia by name and leave a message with him if she is out.',
    flexible: true,
  }),
  callback('cb5', SUPPORTERS[4], {
    assignee: ME,
    requestedBy: LEADERS[2],
    requestedAt: dayAt(-2, 10),
    dueAt: at(4),
    notes: 'Asked for Chiara by name.',
  }),
  callback('cb6', SUPPORTERS[5], { assignee: LEADERS[2], requestedAt: dayAt(-1, 16), dueAt: dayAt(1, 9) }),
  callback('cb7', SUPPORTERS[6], {
    assignee: ME,
    requestedAt: dayAt(-6, 19),
    dueAt: dayAt(2, 18),
    notes: 'Keen to door-knock, needs a lift.',
    flexible: true,
    attempts: 1,
    reschedules: 1,
    lastOutcome: 'no_answer',
  }),
  callback('cb8', SUPPORTERS[7], {
    assignee: null,
    requestedBy: null,
    requestedAt: at(-30),
    origin: ACTION_PAGE,
    dueAt: dayAt(4, 10),
    notes: 'Wants to donate monthly.',
  }),
  callback('cb9', SUPPORTERS[8], {
    assignee: ME,
    requestedAt: dayAt(-1, 12),
    dueAt: dayAt(9, 14),
    notes: 'Back from holiday on the 8th.',
  }),
  callback('cb10', SUPPORTERS[9], {
    assignee: ME,
    requestedAt: dayAt(-2, 17),
    dueAt: at(-3),
    notes: 'Confirm Saturday shift.',
    attempts: 1,
    lastOutcome: 'meaningful_interaction',
    completedAt: at(-2.5),
    completedBy: ME,
  }),
  callback('cb11', SUPPORTERS[10], {
    assignee: LEADERS[1],
    requestedAt: dayAt(-5, 10),
    dueAt: dayAt(-1, 9),
    attempts: 2,
    reschedules: 1,
    lastOutcome: 'meaningful_interaction',
    completedAt: dayAt(-1, 10),
    completedBy: LEADERS[1],
  }),
]

// ---------- Helpers ----------

const SESSION_STATUS: Record<SessionStatus, { label: string; color: 'gray' | 'success' | 'primary' }> = {
  completed: { label: 'Completed', color: 'success' },
  in_progress: { label: 'In Progress', color: 'success' },
  started: { label: 'Started', color: 'primary' },
  not_started: { label: 'Not Started', color: 'gray' },
}

const CALENDAR_OPTIONS = ['Google Calendar', 'Apple Calendar', 'Outlook', 'Office 365', 'Yahoo Calendar']
// A callback is "due now" from 15 minutes before its time until an hour after;
// after that it's overdue and has to be rescheduled before anyone calls.
const DUE_SOON_MINUTES = 15
const GRACE_MINUTES = 60

const firstName = (s: Supporter) => s.fullName.split(' ')[0]

const startOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate())
const endOfWeek = (now: Date) => {
  const d = startOfDay(now)
  d.setDate(d.getDate() + ((7 - d.getDay()) % 7) + 1) // midnight after Sunday
  return d
}
const isSameDay = (a: Date, b: Date) => startOfDay(a).getTime() === startOfDay(b).getTime()
const isThisMonth = (iso: string, now: Date) =>
  new Date(iso).getMonth() === now.getMonth() && new Date(iso).getFullYear() === now.getFullYear()
const minutesUntil = (iso: string, now: Date) => Math.round((new Date(iso).getTime() - now.getTime()) / 60_000)

export function statusFor(cb: Callback, now: Date): CallbackStatus {
  if (cb.completedAt) return 'done'
  const minutes = minutesUntil(cb.dueAt, now)
  if (minutes < -GRACE_MINUTES) return 'overdue'
  if (minutes <= DUE_SOON_MINUTES) return 'due_now'
  return isSameDay(new Date(cb.dueAt), now) ? 'today' : 'upcoming'
}

function matchesFilter(cb: Callback, status: CallbackStatus, filter: Filter, now: Date) {
  switch (filter) {
    case 'open':
      return status !== 'done'
    case 'overdue':
      return status === 'overdue'
    case 'today':
      return status === 'due_now' || status === 'today'
    case 'week':
      return status !== 'done' && status !== 'overdue' && new Date(cb.dueAt) < endOfWeek(now)
    case 'done':
      return !!cb.completedAt && isThisMonth(cb.completedAt, now)
  }
}

const CALLBACK_SCOPE_LABELS: Record<CallbackScope, string> = {
  me: 'Assigned to me',
  groups: 'Any leader',
  all: 'Everyone',
}
const scopesFor = (role: Role): CallbackScope[] => (role === 'admin' ? ['me', 'groups', 'all'] : ['me', 'groups'])

const inScope = (cb: Callback, scope: CallbackScope) => {
  switch (scope) {
    case 'me':
      return cb.assignee?.id === ME.id
    case 'groups':
      return !cb.assignee
    case 'all':
      return true
  }
}
export const isMineOrOpen = (cb: Callback) => !cb.assignee || cb.assignee.id === ME.id

// ---------- Callback date and time (fields shared with the calling session preview) ----------

const newDraft = (now: Date, assigneeId: string = ME.id, notes = '') => newCallbackDraft(now, assigneeId, notes)
const draftError = callbackDraftError

const draftFromCallback = (cb: Callback): CallbackDraft => ({
  date: toDateInput(new Date(cb.dueAt)),
  time: toTimeInput(new Date(cb.dueAt)),
  flexible: cb.flexible,
  assigneeId: cb.assignee?.id ?? ANYONE,
  notes: cb.notes,
})

const assigneeFor = (draft: CallbackDraft): Leader | null => ASSIGNABLE.find((l) => l.id === draft.assigneeId) ?? null

const applyDraft = (cb: Callback, draft: CallbackDraft): Callback => ({
  ...cb,
  dueAt: callbackDueAt(draft).toISOString(),
  flexible: draft.flexible,
  assignee: assigneeFor(draft),
  notes: draft.notes,
})

const assigneePhrase = (assignee: Leader | null) =>
  !assignee ? 'open to any leader' : assignee.id === ME.id ? 'assigned to you' : `assigned to ${assignee.name}`

// A missed callback is rescheduled from a fresh time; anything else opens where it is.
const draftFor = (cb: Callback | null, now: Date): CallbackDraft => {
  if (!cb) return newDraft(now)
  if (statusFor(cb, now) !== 'overdue') return draftFromCallback(cb)
  return { ...newDraft(now, cb.assignee?.id ?? ANYONE, cb.notes), flexible: cb.flexible }
}

// Admins pick anyone from a list; leaders choose between themselves and the group.
const assignFor = (role: Role): AssignConfig =>
  role === 'admin'
    ? { mode: 'admin', people: ASSIGNABLE, meId: ME.id, groupName: GROUP.name }
    : { mode: 'leader', meId: ME.id, meName: ME.name, groupName: GROUP.name }

const CallbackFields = ({
  draft,
  onChange,
  role,
  now,
}: {
  draft: CallbackDraft
  onChange: (draft: CallbackDraft) => void
  role: Role
  now: Date
}) => <ScheduleCallbackFields draft={draft} onChange={onChange} now={now} assign={assignFor(role)} />

// ---------- Dialogs ----------

type ScheduleRequest = { mode: 'new' } | { mode: 'reschedule'; callback: Callback }

const ScheduleForm = ({
  request,
  role,
  now,
  findOpen,
  onSave,
}: {
  request: ScheduleRequest
  role: Role
  now: Date
  findOpen: (supporterId: string) => Callback | undefined
  onSave: (supporterId: string, draft: CallbackDraft, existingId?: string) => void
}) => {
  const rescheduling = request.mode === 'reschedule' ? request.callback : null
  const [supporterId, setSupporterId] = useState<string | null>(rescheduling?.supporter.id ?? null)
  const existing = rescheduling ?? (supporterId ? findOpen(supporterId) : undefined) ?? null
  const [draft, setDraft] = useState<CallbackDraft>(() => draftFor(existing, now))
  const missed = rescheduling && statusFor(rescheduling, now) === 'overdue'
  const moving = !rescheduling && existing
  const supporterOptions = SUPPORTERS.map((sp) => ({
    value: sp.id,
    label: findOpen(sp.id) ? `${sp.fullName} · has a callback` : sp.fullName,
  }))
  const title = rescheduling
    ? `${role === 'admin' ? 'Edit' : 'Reschedule'} callback for ${rescheduling.supporter.fullName}`
    : 'Schedule callback'

  return (
    <>
      <Dialog.Header>
        <Dialog.Title>{title}</Dialog.Title>
        <Dialog.Description className="tw:text-gray-600">
          {rescheduling
            ? missed
              ? `Missed ${whenRange(rescheduling.dueAt, now, rescheduling.flexible)}`
              : `Due ${whenRange(rescheduling.dueAt, now, rescheduling.flexible)}`
            : 'Who asked for a call back, and when?'}
        </Dialog.Description>
      </Dialog.Header>

      {!rescheduling && (
        <LocalAsyncSelect
          name="callbackSupporter"
          label="Supporter"
          options={supporterOptions}
          value={supporterOptions.find((o) => o.value === supporterId) ?? null}
          onChange={(option) => {
            const id = option?.value ?? null
            setSupporterId(id)
            setDraft(draftFor((id && findOpen(id)) || null, now))
          }}
          placeholder="Search supporters by name"
          isClearable
        />
      )}

      {moving && (
        <Alert
          type="info"
          size="sm"
          title={`${firstName(moving.supporter)} already has a callback for ${whenRange(moving.dueAt, now, moving.flexible)}, ${assigneePhrase(moving.assignee)}. Saving moves it; there is only ever one.`}
        />
      )}

      <CallbackFields draft={draft} onChange={setDraft} role={role} now={now} />

      <Dialog.Footer className="tw:mt-2 tw:gap-2">
        <Dialog.Close asChild>
          <Button rank="secondary">Cancel</Button>
        </Dialog.Close>
        <Button
          disabled={!supporterId || !!draftError(draft, now)}
          onClick={() => supporterId && onSave(supporterId, draft, existing?.id)}
        >
          {rescheduling ? 'Save callback' : moving ? 'Move callback' : 'Schedule callback'}
        </Button>
      </Dialog.Footer>
    </>
  )
}

type LogTarget = { callback: Callback; step: 'dial' | 'on_call' | 'log' }

const InitiateCallForm = ({ callback: cb, onStart }: { callback: Callback; onStart: () => void }) => (
  <>
    <Dialog.Header>
      <Dialog.Title>Call {cb.supporter.fullName}</Dialog.Title>
    </Dialog.Header>
    <Alert
      type="info"
      size="sm"
      title="You'll receive a call on your phone number first, then be connected to the supporter."
    />
    <p className="tw:text-sm tw:text-gray-700">
      Click 'Start Call' when you're ready to connect with {cb.supporter.fullName}.
    </p>
    <Dialog.Footer className="tw:mt-2 tw:gap-2">
      <Dialog.Close asChild>
        <Button rank="secondary">Cancel</Button>
      </Dialog.Close>
      <Button leadingIcon="phone-call-01" onClick={onStart}>
        Start Call
      </Button>
    </Dialog.Footer>
  </>
)

function FilterSelect<T extends string>({
  label,
  value,
  onValueChange,
  options,
}: {
  label: string
  value: T
  onValueChange: (value: T) => void
  options: Array<{ value: T; label: string; count?: number }>
}) {
  return (
    <SelectField
      name={`filter-${label.toLowerCase().replace(/\s+/g, '-')}`}
      label={label}
      value={value}
      onValueChange={onValueChange}
      options={options.map((o) => ({
        value: o.value,
        label: o.count === undefined ? o.label : `${o.label} (${o.count})`,
      }))}
    />
  )
}

const linkClass =
  'tw:font-medium tw:text-primary-700 tw:border-b tw:border-transparent tw:hover:text-primary-900 tw:hover:border-current tw:transition-colors'
const formatDate = (iso: string) => dayjs(iso).format('D MMM YYYY')

// ---------- Call lists tab ----------

function CallListsTable({ callLists }: { callLists: CallList[] }) {
  const [sorting, setSorting] = useState<SortingState>([])
  const columns: Array<ColumnDef<CallList>> = useMemo(
    () => [
      {
        accessorKey: 'name',
        header: ({ column }) => <SortableHeader column={column}>Name</SortableHeader>,
        cell: ({ row }) => (
          <div>
            <button type="button" className={linkClass} onClick={() => toast.info(`Would open ${row.original.name}`)}>
              {row.original.name}
            </button>
            {row.original.sublists > 0 && (
              <div className="tw:text-sm tw:text-gray-500">
                {row.original.sublists} {row.original.sublists === 1 ? 'sublist' : 'sublists'}
              </div>
            )}
          </div>
        ),
      },
      {
        accessorKey: 'supporters',
        header: ({ column }) => <SortableHeader column={column}>Supporters</SortableHeader>,
      },
      {
        id: 'createdBy',
        accessorFn: (list) => list.createdBy.name,
        header: ({ column }) => <SortableHeader column={column}>Created by</SortableHeader>,
        cell: ({ row }) => (row.original.createdBy.id === ME.id ? 'You' : row.original.createdBy.name),
        meta: { hideOnMobile: true },
      },
      {
        accessorKey: 'createdAt',
        header: ({ column }) => <SortableHeader column={column}>Created</SortableHeader>,
        cell: ({ row }) => formatDate(row.original.createdAt),
        meta: { hideOnMobile: true },
      },
      {
        id: 'actions',
        header: () => <span className="tw:sr-only">Actions</span>,
        cell: ({ row }) => {
          const list = row.original
          const would = (what: string) => () => toast.info(`Would ${what} ${list.name}`)
          return (
            <div className="tw:flex tw:justify-end tw:gap-2">
              <Button size="sm" rank="secondary" onClick={would('open')}>
                View
              </Button>
              <Button size="sm" rank="secondary" leadingIcon="pencil-01" onClick={would('edit')}>
                Edit
              </Button>
              <DropdownMenu modal={false}>
                <DropdownMenuTrigger asChild>
                  <Button
                    size="sm"
                    rank="secondary"
                    leadingIcon="dots-horizontal"
                    ariaLabel={`More actions for ${list.name}`}
                  />
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="tw:min-w-48">
                  <DropdownMenuItem onSelect={would('add supporters to')}>
                    <Icon type="user-plus-01" className="tw:mr-2 tw:h-4 tw:w-4 tw:text-gray-500" />
                    Add supporters
                  </DropdownMenuItem>
                  <DropdownMenuItem onSelect={would('split')}>
                    <Icon type="columns-03" className="tw:mr-2 tw:h-4 tw:w-4 tw:text-gray-500" />
                    Split list
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onSelect={would('archive')} destructive>
                    <Icon type="archive" className="tw:mr-2 tw:h-4 tw:w-4" />
                    Archive
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          )
        },
        enableSorting: false,
      },
    ],
    []
  )
  const table = useReactTable({
    data: callLists,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    onSortingChange: setSorting,
    state: { sorting },
  })

  return (
    <DataTableRoot table={table}>
      <DataTableContent caption={`Call lists for ${GROUP.name}`} className="tw:bg-white" />
    </DataTableRoot>
  )
}

// ---------- Calling sessions tab (the "My Calling Sessions" table, for one group) ----------

function CallingSessionsTable({ sessions, showLeader }: { sessions: CallingSession[]; showLeader: boolean }) {
  const [sorting, setSorting] = useState<SortingState>([])
  const columns: Array<ColumnDef<CallingSession>> = useMemo(() => {
    const leaderColumn: ColumnDef<CallingSession> = {
      id: 'leader',
      accessorFn: (s) => s.leader.name,
      header: ({ column }) => <SortableHeader column={column}>Leader</SortableHeader>,
      meta: { hideOnMobile: true },
    }
    return [
      {
        accessorKey: 'name',
        header: ({ column }) => <SortableHeader column={column}>Session</SortableHeader>,
        cell: ({ row }) => (
          <div>
            <button type="button" className={linkClass} onClick={() => toast.info(`Would open ${row.original.name}`)}>
              {row.original.name}
            </button>
            <div className="tw:text-sm tw:text-gray-500">{row.original.callList}</div>
          </div>
        ),
      },
      ...(showLeader ? [leaderColumn] : []),
      {
        id: 'completion',
        accessorFn: (s) => (s.total > 0 ? s.completed / s.total : 0),
        header: ({ column }) => <SortableHeader column={column}>Completion</SortableHeader>,
        cell: ({ row }) => {
          const { completed, total } = row.original
          return (
            <div>
              <div className="tw:text-sm tw:font-medium">
                {completed}/{total}
              </div>
              <div className="tw:text-xs tw:text-gray-500">
                {total > 0 ? Math.round((completed / total) * 100) : 0}% complete
              </div>
            </div>
          )
        },
      },
      {
        accessorKey: 'createdAt',
        header: ({ column }) => <SortableHeader column={column}>Created</SortableHeader>,
        cell: ({ row }) => dayjs(row.original.createdAt).format('dddd, D MMMM'),
        meta: { hideOnMobile: true },
      },
      {
        accessorKey: 'status',
        header: ({ column }) => <SortableHeader column={column}>Status</SortableHeader>,
        cell: ({ row }) => {
          const meta = SESSION_STATUS[row.original.status]
          return <Badge color={meta.color}>{meta.label}</Badge>
        },
      },
      {
        id: 'actions',
        header: () => <div className="tw:font-bold">Actions</div>,
        meta: { alignRight: true },
        cell: ({ row }) => (
          <div className="tw:flex tw:justify-end tw:gap-2">
            <Button
              size="sm"
              trailingIcon="arrow-circle-broken-right"
              onClick={() => toast.info(`Would open ${row.original.name}`)}
            >
              Go to session
            </Button>
            <Button
              size="sm"
              rank="secondary"
              color="danger"
              leadingIcon="archive"
              ariaLabel={`Archive ${row.original.name}`}
              onClick={() => toast.info(`Would ask to archive ${row.original.name}`)}
            >
              Archive
            </Button>
          </div>
        ),
        enableSorting: false,
      },
    ]
  }, [showLeader])
  const table = useReactTable({
    data: sessions,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    onSortingChange: setSorting,
    state: { sorting },
  })

  return (
    <DataTableRoot table={table}>
      <DataTableContent caption={`Calling sessions for ${GROUP.name}`} className="tw:bg-white" />
    </DataTableRoot>
  )
}

// ---------- Callback requests (schedule view) ----------

const windowEnd = (cb: Callback) =>
  new Date(new Date(cb.dueAt).getTime() + (cb.flexible ? FLEXIBLE_WINDOW_MINUTES * 60_000 : 0))

function groupBy(callbacks: Callback[], key: (cb: Callback) => string) {
  const groups = new Map<string, Callback[]>()
  for (const cb of callbacks) groups.set(key(cb), [...(groups.get(key(cb)) ?? []), cb])
  return [...groups]
}

const assigneeName = (cb: Callback) => cb.assignee?.name ?? 'Any leader'

// Admins can regroup the schedule by who it's assigned to; it's by day otherwise.
type AssigneeSort = 'asc' | 'desc' | null

function scheduleGroups(callbacks: Callback[], assigneeSort: AssigneeSort) {
  if (!assigneeSort) return groupBy(callbacks, (cb) => toDateInput(new Date(cb.dueAt)))
  const order = assigneeSort === 'asc' ? 1 : -1
  return groupBy(callbacks, assigneeName).sort(([a], [b]) => a.localeCompare(b) * order)
}

const hairline = 'tw:border-r tw:border-gray-200 tw:last:border-r-0'
const assigneeColumn = 'tw:hidden tw:min-[960px]:table-cell'

function CallbackAssignee({ assignee }: { assignee: Leader | null }) {
  return (
    <span
      className={clsx(
        'tw:inline-flex tw:max-w-full tw:items-center tw:gap-1 tw:rounded-full tw:px-2 tw:py-0.5 tw:text-xs tw:font-medium',
        assignee ? 'tw:bg-primary-50 tw:text-primary-700' : 'tw:bg-gray-100 tw:text-gray-700'
      )}
    >
      <Icon type={assignee ? 'user-01' : 'users-01'} className="tw:h-3.5 tw:w-3.5 tw:shrink-0" aria-hidden />
      <span className="tw:truncate">{assignee ? assignee.name : 'Any leader'}</span>
    </span>
  )
}

function CallbackTime({
  callback: cb,
  now,
  missed,
  showDay,
  firstRow,
  lastRow,
}: {
  callback: Callback
  now: Date
  missed: boolean
  // Grouped by assignee, the row says which day it's on.
  showDay: boolean
  firstRow: boolean
  lastRow: boolean
}) {
  const today = isSameDay(new Date(cb.dueAt), now)
  const overdue = !missed && windowEnd(cb) < now

  return (
    <>
      <span
        aria-hidden
        className={clsx(
          'tw:absolute tw:left-[25px] tw:w-0.5 tw:bg-gray-200 tw:sm:left-[29px]',
          firstRow ? 'tw:top-1/2' : 'tw:top-0',
          lastRow ? 'tw:bottom-1/2' : 'tw:-bottom-px'
        )}
      />
      <div className="tw:relative tw:flex tw:items-center tw:gap-3">
        <span aria-hidden className="tw:flex tw:w-7 tw:shrink-0 tw:justify-center">
          <span
            className={clsx(
              'tw:h-2.5 tw:w-2.5 tw:rounded-full tw:ring-4 tw:ring-white tw:group-hover:ring-gray-50',
              overdue || missed ? 'tw:bg-error-500' : today ? 'tw:bg-primary-500' : 'tw:bg-gray-300'
            )}
          />
        </span>
        <span className="tw:min-w-0 tw:leading-tight">
          {showDay && (
            <span className={clsx('tw:block tw:text-xs', overdue || missed ? 'tw:text-error-600' : 'tw:text-gray-600')}>
              {relativeDay(cb.dueAt, now) ?? dayjs(cb.dueAt).format('ddd D MMM')}
            </span>
          )}
          <span
            className={clsx(
              'tw:block tw:font-semibold tw:tabular-nums',
              overdue ? 'tw:text-error-600' : 'tw:text-gray-900'
            )}
          >
            {timeRange(cb.dueAt, cb.flexible)}
            {overdue && <span className="tw:sr-only"> (overdue)</span>}
          </span>
          {missed && (
            <span className="tw:block tw:text-[11px] tw:font-semibold tw:uppercase tw:tracking-wide tw:text-error-600">
              Missed
            </span>
          )}
        </span>
      </div>
    </>
  )
}

function DayHeading({ day, now, columns, first }: { day: string; now: Date; columns: number; first: boolean }) {
  const date = dayjs(day)
  const relative = relativeDay(day, now)
  const tone = date.isSame(now, 'day') ? 'today' : date.isBefore(now, 'day') ? 'past' : 'future'
  return (
    <GroupHeading columns={columns} first={first} tone={tone}>
      {relative && `${relative} · `}
      {date.format('dddd D MMMM')}
    </GroupHeading>
  )
}

function GroupHeading({
  columns,
  first,
  tone,
  children,
}: {
  columns: number
  first: boolean
  tone: 'today' | 'past' | 'future'
  children: ReactNode
}) {
  return (
    <Table.Row className="tw:border-0 tw:hover:bg-transparent">
      <th
        scope="rowgroup"
        colSpan={columns}
        className={clsx(
          'tw:border-b tw:bg-gray-50 tw:px-3 tw:py-2 tw:text-left tw:text-sm tw:font-semibold tw:sm:px-4',
          !first && 'tw:border-t',
          tone === 'today' && 'tw:text-primary-700',
          tone === 'past' && 'tw:text-error-700',
          tone === 'future' && 'tw:text-gray-700'
        )}
      >
        {children}
      </th>
    </Table.Row>
  )
}

export type RowActions = {
  onCall: (cb: Callback) => void
  onLog: (cb: Callback) => void
  onReschedule: (cb: Callback) => void
  onMarkDone: (cb: Callback) => void
}

function CallbackRow({
  callback: cb,
  role,
  now,
  showAssignee,
  showDay,
  highlighted,
  firstRow,
  lastRow,
  actions,
}: {
  callback: Callback
  role: Role
  now: Date
  showAssignee: boolean
  showDay: boolean
  highlighted: boolean
  firstRow: boolean
  lastRow: boolean
  actions: RowActions
}) {
  const canChange = role === 'admin' || !cb.assignee || cb.assignee.id === ME.id
  const missed = canChange && statusFor(cb, now) === 'overdue'
  const rescheduleLabel = role === 'admin' ? 'Edit' : 'Reschedule'
  const subject = `the callback with ${firstName(cb.supporter)}`
  const cell = clsx(hairline, 'tw:h-[52px] tw:border-b tw:px-3 tw:py-3 tw:align-middle tw:sm:px-4')

  return (
    <Table.Row
      id={`callback-${cb.id}`}
      tabIndex={-1}
      className={clsx(
        'tw:group tw:border-0 tw:hover:bg-gray-50 tw:focus:outline-hidden tw:[&:last-child>td]:border-b-0',
        highlighted && 'tw:bg-primary-50 tw:hover:bg-primary-50'
      )}
    >
      <Table.Cell className={clsx(cell, 'tw:relative tw:whitespace-nowrap')}>
        <CallbackTime callback={cb} now={now} missed={missed} showDay={showDay} firstRow={firstRow} lastRow={lastRow} />
      </Table.Cell>
      <Table.Cell className={clsx(cell, 'tw:whitespace-normal')}>
        <div className="tw:flex tw:min-w-0 tw:flex-col">
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type="button"
                className={clsx(linkClass, 'tw:w-fit tw:max-w-full tw:truncate tw:text-left')}
                onClick={() => toast.info(`Would open ${cb.supporter.fullName}`)}
              >
                {cb.supporter.fullName}
              </button>
            </TooltipTrigger>
            <TooltipContent side="bottom" align="start">
              <span>From {cb.origin.label}</span>
              <span>
                Requested {whenLabel(cb.requestedAt, now)} by {cb.requestedBy ? cb.requestedBy.name : 'the supporter'}
              </span>
            </TooltipContent>
          </Tooltip>
          {cb.notes && <CallbackNote note={cb.notes} />}
        </div>
      </Table.Cell>
      {showAssignee && (
        <Table.Cell className={clsx(cell, assigneeColumn)}>
          <div className="tw:flex tw:justify-end">
            <CallbackAssignee assignee={cb.assignee} />
          </div>
        </Table.Cell>
      )}
      <Table.Cell className={cell}>
        <div className="tw:flex tw:items-center tw:justify-end tw:gap-1">
          <SplitButton
            size="sm"
            rank="secondary"
            leadingIcon="phone-call-01"
            label={<span className="tw:hidden tw:sm:inline">Call</span>}
            ariaLabel={`Call ${cb.supporter.fullName}`}
            menuAriaLabel={`More actions for ${cb.supporter.fullName}`}
            className="tw:sm:w-32"
            modal={false}
            onClick={() => actions.onCall(cb)}
          >
            <DropdownMenuItem onSelect={() => actions.onLog(cb)}>
              <Icon type="clipboard-minus" className="tw:mr-2 tw:h-4 tw:w-4 tw:text-gray-500" />
              Log contact
            </DropdownMenuItem>
            <DropdownMenuItem onSelect={() => toast.info(`Would open the SMS dialog for ${firstName(cb.supporter)}`)}>
              <Icon type="message-chat-square" className="tw:mr-2 tw:h-4 tw:w-4 tw:text-gray-500" />
              SMS
            </DropdownMenuItem>
            <DropdownMenuItem onSelect={() => toast.info(`Would open the email dialog for ${firstName(cb.supporter)}`)}>
              <Icon type="mail-01" className="tw:mr-2 tw:h-4 tw:w-4 tw:text-gray-500" />
              Email
            </DropdownMenuItem>
            <DropdownMenuSub>
              <DropdownMenuSubTrigger>
                <Icon type="calendar" className="tw:mr-2 tw:h-4 tw:w-4 tw:text-gray-500" />
                Add to calendar
              </DropdownMenuSubTrigger>
              <DropdownMenuSubContent className="tw:min-w-52">
                {CALENDAR_OPTIONS.map((c) => (
                  <DropdownMenuItem key={c} onSelect={() => toast.info(`Would add ${subject} to ${c}`)}>
                    {c}
                  </DropdownMenuItem>
                ))}
                <DropdownMenuSeparator />
                <DropdownMenuItem onSelect={() => toast.info(`Would download an .ics file of ${subject}`)}>
                  <Icon type="download-cloud" className="tw:mr-2 tw:h-4 tw:w-4 tw:text-gray-500" />
                  Download .ics file
                </DropdownMenuItem>
              </DropdownMenuSubContent>
            </DropdownMenuSub>
            <DropdownMenuSeparator />
            {canChange && (
              <DropdownMenuItem onSelect={() => actions.onReschedule(cb)}>
                <Icon
                  type={role === 'admin' ? 'pencil-01' : 'calendar'}
                  className="tw:mr-2 tw:h-4 tw:w-4 tw:text-gray-500"
                />
                {rescheduleLabel}
              </DropdownMenuItem>
            )}
            <DropdownMenuItem
              onSelect={() => actions.onMarkDone(cb)}
              className="tw:text-success-700 tw:data-highlighted:bg-success-50 tw:data-highlighted:text-success-700"
            >
              <Icon type="check-circle" className="tw:mr-2 tw:h-4 tw:w-4 tw:text-success-600" />
              Mark as done
            </DropdownMenuItem>
          </SplitButton>
        </div>
      </Table.Cell>
    </Table.Row>
  )
}

function CalendarMenu({ count }: { count: number }) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button rank="secondary" leadingIcon="calendar" trailingIcon="chevron-down">
          Calendar
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" sideOffset={8} className="tw:w-80 tw:rounded-xl tw:p-2 tw:font-normal">
        <DropdownMenuIconItem
          icon="calendar"
          label="Subscribe to my callbacks"
          description="Adds them to your calendar and stays up to date as they move or close."
          onSelect={() => toast.info('Would open the calendar subscription link')}
        />
        <DropdownMenuIconItem
          icon="download-cloud"
          label={`Download the ${count} shown`}
          description="A one-off .ics file. It won't follow later changes."
          onSelect={() => toast.info(`Would download an .ics file of ${count} callbacks`)}
        />
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

function SortButton({
  sorted,
  onClick,
  children,
}: {
  sorted: 'asc' | 'desc' | false
  onClick: () => void
  children: ReactNode
}) {
  return (
    <button
      type="button"
      className="tw:inline-flex tw:items-center tw:gap-1 tw:font-bold tw:hover:text-gray-900"
      onClick={onClick}
    >
      {children}
      <Icon
        type={sorted === 'desc' ? 'sort-descending' : 'sort-ascending'}
        width={14}
        height={14}
        className={sorted ? undefined : 'tw:text-gray-400'}
        aria-hidden
      />
    </button>
  )
}

const ariaSort = (sorted: 'asc' | 'desc' | false) =>
  sorted === 'asc' ? 'ascending' : sorted === 'desc' ? 'descending' : undefined

export function CallbackSchedule({
  callbacks,
  role,
  now,
  showAssignee,
  sortable = false,
  highlightedId,
  actions,
}: {
  callbacks: Callback[]
  role: Role
  now: Date
  showAssignee: boolean
  // Admins viewing everyone's callbacks can sort them by assignee.
  sortable?: boolean
  highlightedId: string | null
  actions: RowActions
}) {
  const [assigneeSortChoice, setAssigneeSort] = useState<AssigneeSort>(null)
  const assigneeSort = sortable ? assigneeSortChoice : null
  const byTime = assigneeSort ? false : 'asc'
  const head = clsx(hairline, 'tw:whitespace-nowrap tw:border-b tw:px-3 tw:font-bold tw:sm:px-4')
  const columns = showAssignee ? 4 : 3

  return (
    <TooltipProvider>
      <div className="tw:overflow-clip tw:rounded-md tw:border tw:bg-white">
        <table className="tw:w-full tw:table-fixed tw:border-separate tw:border-spacing-0 tw:text-sm">
          <Table.Caption className="tw:sr-only">Callback requests for {GROUP.name}, by due time</Table.Caption>
          <Table.Header>
            <Table.Row className="tw:border-0 tw:hover:bg-transparent">
              <Table.Head
                className={clsx(head, 'tw:w-28 tw:sm:w-48')}
                aria-sort={sortable ? ariaSort(byTime) : undefined}
              >
                {sortable ? (
                  <SortButton sorted={byTime} onClick={() => setAssigneeSort(null)}>
                    Date &amp; Time
                  </SortButton>
                ) : (
                  'Date & Time'
                )}
              </Table.Head>
              <Table.Head className={head}>Supporter</Table.Head>
              {showAssignee && (
                <Table.Head
                  className={clsx(head, assigneeColumn, 'tw:w-[196px] tw:text-right')}
                  aria-sort={sortable ? ariaSort(assigneeSort ?? false) : undefined}
                >
                  {sortable ? (
                    <SortButton
                      sorted={assigneeSort ?? false}
                      onClick={() => setAssigneeSort(assigneeSort === 'asc' ? 'desc' : 'asc')}
                    >
                      Assigned to
                    </SortButton>
                  ) : (
                    'Assigned to'
                  )}
                </Table.Head>
              )}
              <Table.Head className={clsx(head, 'tw:w-28 tw:text-right tw:sm:w-44')}>Actions</Table.Head>
            </Table.Row>
          </Table.Header>
          {scheduleGroups(callbacks, assigneeSort).map(([group, rows], groupIndex) => (
            <Table.Body key={group}>
              {assigneeSort ? (
                <GroupHeading columns={columns} first={groupIndex === 0} tone="future">
                  {group} · {rows.length}
                </GroupHeading>
              ) : (
                <DayHeading day={group} now={now} columns={columns} first={groupIndex === 0} />
              )}
              {rows.map((cb, index) => (
                <CallbackRow
                  key={cb.id}
                  callback={cb}
                  role={role}
                  now={now}
                  showAssignee={showAssignee}
                  showDay={!!assigneeSort}
                  highlighted={highlightedId === cb.id}
                  firstRow={index === 0}
                  lastRow={index === rows.length - 1}
                  actions={actions}
                />
              ))}
            </Table.Body>
          ))}
        </table>
      </div>
    </TooltipProvider>
  )
}

// ---------- Page ----------

const TAB_VALUES: TabValue[] = ['overview', 'call_lists', 'calling_sessions', 'callbacks']
const initialTab = (): TabValue => {
  const fromUrl = new URLSearchParams(window.location.search).get('tab') as TabValue | null
  return fromUrl && fromUrl !== 'overview' && TAB_VALUES.includes(fromUrl) ? fromUrl : 'call_lists'
}

const CallbackRequestsPreview = () => {
  const [now] = useState(() => new Date())
  const [callbacks, setCallbacks] = useState<Callback[]>(INITIAL_CALLBACKS)
  const [tab, setTabState] = useState<TabValue>(initialTab)

  // Design tools
  const [showDesign, setShowDesign] = useState(false)
  const [role, setRole] = useState<Role>('leader')
  const [dataset, setDataset] = useState<'sample' | 'empty'>('sample')
  // Off by default: the Overview tab is still being explored.
  const [showOverview, setShowOverview] = useState(false)

  // Callback list controls
  const [scopeChoice, setScopeChoice] = useState<CallbackScope>('me')
  // Admins can also show one person's callbacks, e.g. to reassign them while that person is away.
  const [personChoice, setPerson] = useState<string | null>(null)
  const setScope = (next: CallbackScope) => {
    setScopeChoice(next)
    setPerson(null)
  }
  const [search, setSearch] = useState('')

  // Dialogs
  const [scheduleRequest, setScheduleRequest] = useState<ScheduleRequest | null>(null)
  const [logTarget, setLogTarget] = useState<LogTarget | null>(null)
  const [focusId, setFocusId] = useState<string | null>(() =>
    new URLSearchParams(window.location.search).get('callback')
  )

  const setTab = useCallback((value: TabValue) => {
    setTabState(value)
    const params = new URLSearchParams(window.location.search)
    params.set('tab', value)
    history.replaceState(history.state, document.title, `?${params.toString()}`)
  }, [])

  const TabLink = useMemo(
    () =>
      function TabLink({ href, ...props }: ComponentProps<'a'> & { href: string }) {
        return (
          <a
            href={href}
            onClick={(e) => {
              e.preventDefault()
              setTab(new URL(href, window.location.href).searchParams.get('tab') as TabValue)
            }}
            {...props}
          />
        )
      },
    [setTab]
  )

  // Leaders only ever see their own callbacks and sessions.
  // Switching to Leader drops an admin-only choice back to the default.
  const scope: CallbackScope = scopesFor(role).includes(scopeChoice) ? scopeChoice : 'groups'
  const person = role === 'admin' && scope === 'all' ? personChoice : null

  const empty = dataset === 'empty'
  const allCallbacks = useMemo(
    () => (empty ? [] : callbacks).map((cb) => ({ callback: cb, status: statusFor(cb, now) })),
    [callbacks, empty, now]
  )
  const callLists = empty ? [] : CALL_LISTS
  const sessions = empty ? [] : CALLING_SESSIONS

  const countsFor = (s: CallbackScope | 'mine') => {
    const scoped = allCallbacks.filter(({ callback: cb }) => (s === 'mine' ? isMineOrOpen(cb) : inScope(cb, s)))
    const count = (f: Filter) => scoped.filter(({ callback: cb, status }) => matchesFilter(cb, status, f, now)).length
    return {
      open: count('open'),
      overdue: count('overdue'),
      today: count('today'),
      week: count('week'),
      done: count('done'),
    }
  }
  const scopedCounts = countsFor(scope)
  // Overview: leaders see their own numbers, captains the whole group's.
  const overviewScope = role === 'admin' ? 'all' : 'mine'
  const overviewCounts = countsFor(overviewScope)

  const term = search.trim().toLowerCase()
  const visible = allCallbacks
    .filter(({ callback: cb, status }) => status !== 'done' && inScope(cb, scope))
    .filter(({ callback: cb }) => !person || cb.assignee?.id === person)
    .filter(({ callback: cb }) => !term || cb.supporter.fullName.toLowerCase().includes(term))
    .map(({ callback: cb }) => cb)
    .sort((a, b) => a.dueAt.localeCompare(b.dueAt))

  const mine = allCallbacks.filter(({ callback: cb }) => isMineOrOpen(cb))
  // What the calling list will do with each callback: due ones jump to the top,
  // later ones stay hidden until they're due, missed ones need rescheduling.
  const queue = mine
    .filter(({ status }) => status !== 'done')
    .sort((a, b) => a.callback.dueAt.localeCompare(b.callback.dueAt))
    .slice(0, 5)

  const activeSessions = sessions.filter((s) => s.status === 'in_progress' || s.status === 'started')

  // ---------- Actions ----------

  const update = (id: string, fn: (cb: Callback) => Callback) =>
    setCallbacks((all) => all.map((cb) => (cb.id === id ? fn(cb) : cb)))

  const openCallbackFor = (supporterId: string) =>
    callbacks.find((cb) => cb.supporter.id === supporterId && !cb.completedAt)

  const saveSchedule = (supporterId: string, draft: CallbackDraft, existingId?: string) => {
    const description = draftWhen(draft, now)
    const id = existingId ?? `cb-new-${Date.now()}`
    if (existingId) {
      update(existingId, (cb) => applyDraft({ ...cb, reschedules: cb.reschedules + 1 }, draft))
      toast.success(scheduleRequest?.mode === 'reschedule' ? 'Callback rescheduled' : 'Callback moved', { description })
    } else {
      const person = SUPPORTERS.find((s) => s.id === supporterId) ?? SUPPORTERS[0]
      setCallbacks((all) => [
        ...all,
        applyDraft(
          callback(id, person, {
            assignee: null,
            requestedBy: ME,
            requestedAt: new Date().toISOString(),
            origin: { kind: 'manual', label: 'Added by hand' },
            dueAt: '',
          }),
          draft
        ),
      ])
      toast.success(`Callback scheduled with ${firstName(person)}`, { description })
    }
    setScheduleRequest(null)
    openCallback(id, assigneeFor(draft))
  }

  const saveContact = ({ outcome, callback: draft, completeCallback }: LoggedContact) => {
    if (!logTarget) return
    const { callback: target } = logTarget
    update(target.id, (cb) => {
      const tried = { ...cb, attempts: cb.attempts + 1, lastOutcome: outcome }
      if (draft) return applyDraft({ ...tried, reschedules: cb.reschedules + 1 }, draft)
      if (completeCallback) return { ...tried, completedAt: new Date().toISOString(), completedBy: ME }
      return tried
    })
    toast.success(
      draft
        ? `Callback moved with ${firstName(target.supporter)}`
        : completeCallback
          ? 'Contact logged, callback done'
          : `Contact logged. ${firstName(target.supporter)} stays on your list.`,
      { description: draft ? draftWhen(draft, now) : undefined }
    )
    setLogTarget(null)
  }

  const markDone = (target: Callback) => {
    update(target.id, (cb) => ({
      ...cb,
      lastOutcome: 'marked_done',
      completedAt: new Date().toISOString(),
      completedBy: ME,
    }))
    toast.success(`${firstName(target.supporter)}'s callback marked as done`, {
      action: { label: 'Undo', onClick: () => update(target.id, () => target) },
    })
  }

  const openCallback = (id: string, assignee: Leader | null) => {
    setScope(!assignee ? 'groups' : assignee.id === ME.id ? 'me' : 'all')
    setSearch('')
    setTab('callbacks')
    setFocusId(id)
  }

  useEffect(() => {
    if (!focusId || tab !== 'callbacks') return
    const frame = requestAnimationFrame(() => {
      const el = document.getElementById(`callback-${focusId}`)
      el?.scrollIntoView({ behavior: 'smooth', block: 'center' })
      el?.focus({ preventScroll: true })
    })
    const timer = setTimeout(() => setFocusId(null), 2500)
    return () => {
      cancelAnimationFrame(frame)
      clearTimeout(timer)
    }
  }, [focusId, tab])

  // ---------- Tabs ----------

  const overviewTab = (
    <div className="tw:flex tw:flex-col tw:gap-8">
      <section aria-labelledby="overview-callbacks" className="tw:flex tw:flex-col tw:gap-3">
        <div className="tw:flex tw:flex-wrap tw:items-end tw:justify-between tw:gap-2">
          <div>
            <h2 id="overview-callbacks" className="tw:text-base tw:font-bold tw:text-gray-900">
              Callback requests
            </h2>
            <p className="tw:text-sm tw:text-gray-500">
              {overviewScope === 'all' ? `Across ${GROUP.name}.` : 'Assigned to you, or to anyone in the group.'}
            </p>
          </div>
          <Button rank="link" size="sm" trailingIcon="arrow-narrow-right" onClick={() => setTab('callbacks')}>
            View callback requests
          </Button>
        </div>
        <div className="tw:grid tw:grid-cols-2 tw:gap-3 tw:md:grid-cols-4">
          <SimpleStatCard value={overviewCounts.overdue} label="Overdue" />
          <SimpleStatCard value={overviewCounts.today} label="Due today" />
          <SimpleStatCard value={overviewCounts.week} label="This week" />
          <SimpleStatCard value={overviewCounts.done} label="Completed this month" />
        </div>
      </section>

      <section aria-labelledby="overview-calling" className="tw:flex tw:flex-col tw:gap-3">
        <h2 id="overview-calling" className="tw:text-base tw:font-bold tw:text-gray-900">
          Calling
        </h2>
        <div className="tw:grid tw:gap-3 tw:md:grid-cols-2">
          <SplitStatCard value={activeSessions.length} label="Active sessions">
            <BreakdownLine
              icon="play-circle"
              iconColor="tw:text-success-600"
              label={`${activeSessions.reduce((n, s) => n + (s.total - s.completed), 0)} supporters left to call`}
            />
            <BreakdownLine
              icon="clock"
              iconColor="tw:text-gray-400"
              label={`${sessions.filter((s) => s.status === 'not_started').length} not started yet`}
            />
            <BreakdownLine
              icon="check-circle"
              iconColor="tw:text-primary-600"
              label={`${sessions.filter((s) => s.status === 'completed' && isThisMonth(s.createdAt, now)).length} completed this month`}
            />
          </SplitStatCard>
          <SplitStatCard value={callLists.length} label="Call lists">
            <BreakdownLine
              icon="users-01"
              iconColor="tw:text-primary-600"
              label={`${callLists.reduce((n, l) => n + l.supporters, 0)} supporters across them`}
            />
            <BreakdownLine
              icon="clipboard-minus"
              iconColor="tw:text-gray-400"
              label={`${callLists.reduce((n, l) => n + l.sublists, 0)} sublists`}
            />
          </SplitStatCard>
        </div>
      </section>

      <section aria-labelledby="overview-queue" className="tw:flex tw:flex-col tw:gap-3">
        <div>
          <h2 id="overview-queue" className="tw:text-base tw:font-bold tw:text-gray-900">
            Callback queue
          </h2>
          <p className="tw:text-sm tw:text-gray-500">
            How your calling list will order callbacks. Due ones jump to the top, later ones stay hidden until they are
            due. Select one to open it.
          </p>
        </div>
        {queue.length === 0 ? (
          <p className="tw:text-sm tw:text-gray-500">No open callbacks.</p>
        ) : (
          <ul aria-label="Callback queue" className="tw:flex tw:flex-col tw:gap-2">
            {queue.map(({ callback: cb, status }) => {
              const due = status === 'due_now'
              const missed = status === 'overdue'
              return (
                <li key={cb.id}>
                  <button
                    type="button"
                    onClick={() => openCallback(cb.id, cb.assignee)}
                    className={clsx(
                      'tw:flex tw:w-full tw:items-center tw:gap-3 tw:rounded-lg tw:border tw:px-4 tw:py-3 tw:text-left tw:text-sm',
                      'tw:focus-visible:outline-hidden tw:focus-visible:ring-4 tw:focus-visible:ring-primary-300',
                      due && 'tw:border-warning-300 tw:bg-warning-50 tw:hover:bg-warning-100',
                      missed && 'tw:border-error-300 tw:bg-error-50 tw:hover:bg-error-100',
                      !due && !missed && 'tw:border-gray-200 tw:bg-white tw:hover:bg-gray-50'
                    )}
                  >
                    <span
                      aria-hidden
                      className={clsx(
                        'tw:h-2.5 tw:w-2.5 tw:shrink-0 tw:rounded-full',
                        due ? 'tw:bg-warning-500' : missed ? 'tw:bg-error-500' : 'tw:bg-gray-300'
                      )}
                    />
                    <span className={clsx('tw:min-w-0 tw:flex-1', !due && !missed && 'tw:text-gray-600')}>
                      <b className={clsx('tw:font-semibold', (due || missed) && 'tw:text-gray-900')}>
                        {cb.supporter.fullName}
                      </b>
                      {' · '}
                      {due || missed
                        ? cb.requestedBy
                          ? 'call back'
                          : 'asked for a call back'
                        : `call back ${whenRange(cb.dueAt, now, cb.flexible)}`}{' '}
                      {cb.assignee ? (
                        cb.assignee.id === ME.id && (
                          <span className="tw:ml-1 tw:text-xs tw:font-semibold tw:text-warning-700">to me</span>
                        )
                      ) : (
                        <span className="tw:ml-1 tw:rounded-full tw:bg-primary-50 tw:px-2 tw:py-0.5 tw:text-xs tw:font-semibold tw:text-primary-700">
                          any leader
                        </span>
                      )}
                    </span>
                    <span
                      className={clsx(
                        'tw:shrink-0 tw:font-mono tw:text-xs',
                        due ? 'tw:text-warning-700' : missed ? 'tw:text-error-700' : 'tw:text-gray-500'
                      )}
                    >
                      {due
                        ? `due ${timeRange(cb.dueAt, cb.flexible)}`
                        : missed
                          ? 'missed · reschedule'
                          : 'hidden until due'}
                    </span>
                  </button>
                </li>
              )
            })}
          </ul>
        )}
      </section>
    </div>
  )

  const callListsTab = (
    <div className="tw:flex tw:flex-col tw:gap-4">
      {callLists.length === 0 ? (
        <EmptyState
          iconType="clipboard-minus"
          headline="No call lists yet"
          subtext="Make a call list to choose who to phone, then start a calling session from it."
        >
          <Button leadingIcon="plus" onClick={() => toast.info('Would open the new call list dialog')}>
            Add call list
          </Button>
        </EmptyState>
      ) : (
        <CallListsTable callLists={callLists} />
      )}
    </div>
  )

  const callingSessionsTab = (
    <div className="tw:flex tw:flex-col tw:gap-4">
      {sessions.length === 0 ? (
        <EmptyState
          iconType="phone-call-01"
          headline="No calling sessions yet"
          subtext="Create your first calling session to start contacting supporters"
        />
      ) : (
        <CallingSessionsTable sessions={sessions} showLeader />
      )}
    </div>
  )

  const scopeOptions = scopesFor(role).map((sc) => ({
    value: sc,
    label: CALLBACK_SCOPE_LABELS[sc],
    count: countsFor(sc).open,
  }))

  // Admins pick whose callbacks from one list: the scopes, then each person.
  const openAll = allCallbacks.filter(({ status }) => status !== 'done').map(({ callback }) => callback)
  const showingOptions = [
    ...scopeOptions,
    ...ASSIGNABLE.filter((p) => p.id !== ME.id).map((p) => ({
      value: p.id,
      label: p.name,
      count: openAll.filter((cb) => cb.assignee?.id === p.id).length,
    })),
  ]
  const showWhose = (value: string) => {
    if ((scopesFor(role) as string[]).includes(value)) return setScope(value as CallbackScope)
    setScopeChoice('all')
    setPerson(value)
  }

  const callbacksTab = (
    <div className="tw:flex tw:flex-col tw:gap-3.5">
      <div className="tw:flex tw:flex-col tw:gap-3 tw:sm:flex-row tw:sm:items-end tw:sm:justify-between">
        <div className="tw:w-full tw:sm:w-auto tw:sm:flex-1 tw:min-[840px]:w-[400px] tw:min-[840px]:flex-none">
          <TextInput
            name="callback_search"
            type="search"
            label="Search by name"
            leadingIcon="search-lg"
            inputWrapperClassName="tw:border-gray-300"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <div className="tw:flex tw:items-end tw:gap-3">
          {role === 'admin' ? (
            <div className="tw:flex-1 tw:sm:min-w-56 tw:sm:flex-none">
              <FilterSelect<string>
                label="Showing"
                value={person ?? scope}
                onValueChange={showWhose}
                options={showingOptions}
              />
            </div>
          ) : (
            <>
              <SegmentedControl<CallbackScope>
                ariaLabel="Whose callbacks"
                options={scopeOptions}
                value={scope}
                onChange={setScope}
                className="tw:hidden tw:sm:inline-flex"
              />
              <div className="tw:flex-1 tw:sm:hidden">
                <FilterSelect<CallbackScope>
                  label="Showing"
                  value={scope}
                  onValueChange={setScope}
                  options={scopeOptions}
                />
              </div>
            </>
          )}
          <CalendarMenu count={visible.length} />
        </div>
      </div>

      {visible.length === 0 ? (
        <div className="tw:flex tw:flex-col tw:items-center tw:gap-2 tw:rounded-xl tw:border tw:border-gray-200 tw:bg-white tw:px-5 tw:py-12 tw:text-center">
          <Icon type="phone-call-01" className="tw:h-8 tw:w-8 tw:text-gray-cool-400" />
          <p className="tw:font-medium tw:text-gray-900">
            {empty
              ? 'No callback requests yet'
              : search
                ? `No callback requests match “${search}”`
                : 'No open callback requests'}
          </p>
          <p className="tw:max-w-sm tw:text-sm tw:text-gray-600">
            When someone asks for a call back during a calling session, or through an action page, it lands here.
          </p>
        </div>
      ) : (
        <CallbackSchedule
          callbacks={visible}
          role={role}
          now={now}
          showAssignee={scope !== 'me'}
          sortable={role === 'admin' && scope === 'all' && !person}
          highlightedId={focusId}
          actions={{
            onCall: (cb) => setLogTarget({ callback: cb, step: 'dial' }),
            onLog: (cb) => setLogTarget({ callback: cb, step: 'log' }),
            onReschedule: (cb) => setScheduleRequest({ mode: 'reschedule', callback: cb }),
            onMarkDone: markDone,
          }}
        />
      )}
    </div>
  )

  const tabs: Array<{ value: TabValue; label: string; count?: number; content: ReactNode }> = [
    { value: 'overview', label: 'Overview', content: overviewTab },
    { value: 'call_lists', label: 'Call lists', content: callListsTab },
    { value: 'calling_sessions', label: 'Calling sessions', content: callingSessionsTab },
    { value: 'callbacks', label: 'Callback requests', count: scopedCounts.open, content: callbacksTab },
  ]

  const visibleTabs = showOverview ? tabs : tabs.filter((t) => t.value !== 'overview')
  const activeTab = visibleTabs.some((t) => t.value === tab) ? tab : visibleTabs[0].value

  return (
    <PageLayout.Root>
      <PageLayout.Header
        heading="Calls"
        icon="phone-call-01"
        subtitle={GROUP.name}
        actions={
          activeTab === 'call_lists' ? (
            <Button leadingIcon="plus" onClick={() => toast.info('Would open the new call list dialog')}>
              Add call list
            </Button>
          ) : activeTab === 'calling_sessions' ? null : (
            <Button leadingIcon="plus" onClick={() => setScheduleRequest({ mode: 'new' })}>
              Schedule callback
            </Button>
          )
        }
        nav={
          <PageHeaderNav
            ariaLabel="Calls"
            linkComponent={TabLink}
            items={visibleTabs.map((t) => ({
              label: t.label,
              href: `?tab=${t.value}`,
              current: t.value === activeTab,
              count: t.count,
            }))}
          />
        }
      />
      <PageLayout.Body>{visibleTabs.find((t) => t.value === activeTab)?.content}</PageLayout.Body>

      <Dialog.Root open={!!scheduleRequest} onOpenChange={(open) => !open && setScheduleRequest(null)}>
        <Dialog.Content size="xl" scroll>
          {scheduleRequest && (
            <ScheduleForm
              request={scheduleRequest}
              role={role}
              now={now}
              findOpen={openCallbackFor}
              onSave={saveSchedule}
            />
          )}
        </Dialog.Content>
      </Dialog.Root>

      <Dialog.Root open={!!logTarget} onOpenChange={(open) => !open && setLogTarget(null)}>
        <Dialog.Content size="xl" scroll>
          {logTarget &&
            (logTarget.step === 'dial' ? (
              <InitiateCallForm
                callback={logTarget.callback}
                onStart={() => setLogTarget({ ...logTarget, step: 'on_call' })}
              />
            ) : (
              <LogContactForm
                supporter={{ ...logTarget.callback.supporter, firstName: firstName(logTarget.callback.supporter) }}
                openCallback={draftFromCallback(logTarget.callback)}
                ownerName={logTarget.callback.assignee?.name ?? 'Any leader'}
                meId={ME.id}
                now={now}
                assign={assignFor(role)}
                onCall={logTarget.step === 'on_call'}
                onSave={saveContact}
              />
            ))}
        </Dialog.Content>
      </Dialog.Root>

      {/* Design tools: dev-only floating overlay, portaled to body, default closed */}
      {createPortal(
        <>
          <button
            onClick={() => setShowDesign((s) => !s)}
            className="tw:fixed tw:bottom-4 tw:left-4 tw:z-50 tw:flex tw:items-center tw:gap-2 tw:rounded-full tw:border tw:border-gray-300 tw:bg-white tw:px-3.5 tw:py-2 tw:text-sm tw:font-medium tw:text-gray-600 tw:shadow-md tw:hover:border-primary-400 tw:hover:text-primary-600"
          >
            <Icon type="eye" className="tw:h-4 tw:w-4" />
            {showDesign ? 'Hide design tools' : 'Design tools'}
          </button>
          {showDesign && (
            <div className="tw:fixed tw:bottom-16 tw:left-4 tw:z-50 tw:w-[min(720px,calc(100vw-2rem))] tw:rounded-xl tw:border tw:border-dashed tw:border-gray-300 tw:bg-white tw:p-5 tw:shadow-xl">
              <div className="tw:mb-2 tw:flex tw:items-center tw:gap-2">
                <Icon type="eye" className="tw:h-4 tw:w-4 tw:text-gray-400" />
                <span className="tw:text-sm tw:font-bold tw:text-gray-700">Design exploration</span>
                <button
                  onClick={() => setShowDesign(false)}
                  aria-label="Hide design tools"
                  className="tw:ml-auto tw:flex tw:h-8 tw:w-8 tw:items-center tw:justify-center tw:rounded-lg tw:border tw:border-gray-300 tw:bg-white tw:text-gray-500 tw:hover:border-primary-400 tw:hover:text-primary-600"
                >
                  <Icon type="x-close" className="tw:h-4 tw:w-4" />
                </button>
              </div>
              <p className="tw:mb-4 tw:text-xs tw:leading-relaxed tw:text-gray-500">
                This panel is purely for exploring the design and is not part of the product.
                <br />
                Questo pannello serve solo a esplorare il design e non fa parte del prodotto.
              </p>
              <div className="tw:flex tw:flex-wrap tw:gap-8">
                <SegControl
                  label="Viewing as"
                  value={role}
                  onChange={(v) => setRole(v as Role)}
                  options={[
                    { value: 'leader', label: 'Leader' },
                    { value: 'admin', label: 'Admin' },
                  ]}
                />
                <SegControl
                  label="Overview tab"
                  value={showOverview ? 'on' : 'off'}
                  onChange={(v) => setShowOverview(v === 'on')}
                  options={[
                    { value: 'off', label: 'Off' },
                    { value: 'on', label: 'On' },
                  ]}
                />
                <SegControl
                  label="Data"
                  value={dataset}
                  onChange={(v) => setDataset(v as typeof dataset)}
                  options={[
                    { value: 'sample', label: 'Sample' },
                    { value: 'empty', label: 'Empty' },
                  ]}
                />
              </div>
            </div>
          )}
        </>,
        document.body
      )}
    </PageLayout.Root>
  )
}

function SegControl({
  label,
  value,
  options,
  onChange,
}: {
  label: string
  value: string
  options: Array<{ value: string; label: string }>
  onChange: (v: string) => void
}) {
  return (
    <div className="tw:flex tw:flex-col tw:gap-1.5">
      <span className="tw:text-xs tw:font-bold tw:uppercase tw:tracking-wide tw:text-gray-400">{label}</span>
      <div className="tw:inline-flex tw:w-fit tw:overflow-hidden tw:rounded-lg tw:border tw:border-gray-300">
        {options.map((o, i) => (
          <button
            key={o.value}
            onClick={() => onChange(o.value)}
            aria-pressed={value === o.value}
            className={clsx(
              'tw:px-3 tw:py-1.5 tw:text-sm tw:font-medium',
              i > 0 && 'tw:border-l tw:border-gray-300',
              value === o.value
                ? 'tw:bg-primary-50 tw:text-primary-700'
                : 'tw:bg-white tw:text-gray-600 tw:hover:bg-gray-50'
            )}
          >
            {o.label}
          </button>
        ))}
      </div>
    </div>
  )
}

export default CallbackRequestsPreview
